import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./UI";
import {
  getPendingPwaUiState,
  reloadAppForUpdate,
  requestAppRefresh,
  subscribeToPwaUiState,
} from "../serviceWorkerRegistration";

const PWAUpdatePrompt = () => {
  const [show, setShow] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isForcedUpdate, setIsForcedUpdate] = useState(false);
  const [updateReason, setUpdateReason] = useState("update_available");
  const [isRecoveryPrompt, setIsRecoveryPrompt] = useState(false);

  useEffect(() => {
    const applyPendingState = (detail) => {
      if (!detail) {
        return;
      }
      setWaitingWorker(detail.waitingWorker || null);
      setIsForcedUpdate(Boolean(detail.isForced));
      setUpdateReason(detail.reason || "update_available");
      setIsRecoveryPrompt(Boolean(detail.isRecovery));
      setIsUpdating(Boolean(detail.showUpdating) && !detail.isRecovery);
      setShow(true);
    };

    const handleControllerChange = () => {
      if (navigator.serviceWorker.controller) {
        setIsRecoveryPrompt(false);
        setShow(true);
        setIsUpdating(true);
        reloadAppForUpdate({ reason: "controllerchange" });
      }
    };

    applyPendingState(getPendingPwaUiState());
    const unsubscribe = subscribeToPwaUiState(applyPendingState);
    navigator.serviceWorker?.addEventListener(
      "controllerchange",
      handleControllerChange,
    );

    return () => {
      unsubscribe();
      navigator.serviceWorker?.removeEventListener(
        "controllerchange",
        handleControllerChange,
      );
    };
  }, []);

  const onUpdate = () => {
    setIsUpdating(true);

    // Recovery-цикл: если кэши не снести, старый SW снова отдаст битый index.html
    // (ссылающийся на удалённый бандл) -> бесконечное «Восстановить приложение».
    // Поэтому в recovery-режиме принудительно удаляем ВСЕ кэши ДО перезагрузки.
    if (isRecoveryPrompt && "caches" in window) {
      caches
        .keys()
        .then((names) =>
          Promise.all(
            names
              .filter((n) => n.indexOf("school-nutrition") === 0)
              .map((n) => caches.delete(n)),
          ),
        )
        .catch(() => {});
    }

    requestAppRefresh({
      waitingWorker,
      reason: updateReason,
    });
  };

  const title = isRecoveryPrompt
    ? "Нужно восстановить приложение"
    : isForcedUpdate
      ? "Требуется обновление"
      : "Доступно обновление";
  const description = isRecoveryPrompt
    ? "Во время запуска или обновления часть файлов приложения загрузилась некорректно. Попробуйте восстановить приложение."
    : isForcedUpdate
    ? "Текущая версия приложения больше не поддерживается. Обновите приложение, чтобы продолжить работу."
    : "Новая версия приложения готова к установке.";
  const progressText = isRecoveryPrompt
    ? "Пожалуйста, подождите... Мы пытаемся восстановить рабочую версию приложения."
    : isForcedUpdate
    ? "Пожалуйста, подождите... Мы применяем обязательное обновление приложения."
    : "Пожалуйста, подождите... Это займет около 5 секунд.";

  return (
    <>
      <AnimatePresence>
        {show && !isUpdating && (isForcedUpdate || isRecoveryPrompt) && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-24 left-4 right-4 md:left-auto md:right-8 md:bottom-8 z-[var(--z-index-notifications)] max-w-sm"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25 }}
              className="bg-gradient-to-br from-[var(--color-bg-secondary)] to-[var(--color-bg-primary)] border-2 border-[var(--color-primary)]/50 shadow-[0_25px_60px_rgba(0,0,0,0.4)] rounded-[2rem] p-6 backdrop-blur-2xl"
            >
              <div className="flex items-center gap-4 mb-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.1, type: "spring" }}
                  className="w-16 h-16 bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-primary-dark)] rounded-2xl flex items-center justify-center text-white shadow-xl shadow-[var(--color-primary)]/30"
                >
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </motion.div>
                <div>
                  <h3 className="text-xl font-black text-[var(--color-text-primary)] leading-tight">
                    {title}
                  </h3>
                  <p className="text-sm text-[var(--color-text-secondary)] font-medium mt-1 leading-relaxed">
                    {description}
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="primary"
                  className="flex-1 rounded-xl h-12 font-black tracking-wide text-base shadow-lg shadow-[var(--color-primary)]/30 hover:shadow-[var(--color-primary)]/40"
                  onClick={onUpdate}
                  style={{
                    background:
                      "linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%)",
                  }}
                >
                  {isRecoveryPrompt ? "ВОССТАНОВИТЬ" : "ОБНОВИТЬ"}
                </Button>
                {!isForcedUpdate && !isRecoveryPrompt && (
                  <Button
                    variant="ghost"
                    className="px-6 text-[var(--color-text-tertiary)] font-bold rounded-xl h-12"
                    onClick={() => setShow(false)}
                  >
                    Позже
                  </Button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isUpdating && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[var(--z-index-max)] bg-[var(--color-bg-primary)]/95 backdrop-blur-xl flex flex-col items-center justify-center text-center p-8"
          >
            <motion.div
              animate={{
                rotate: 360,
                scale: [1, 1.05, 1],
              }}
              transition={{
                rotate: { duration: 3, repeat: Infinity, ease: "linear" },
                scale: { duration: 1.5, repeat: Infinity },
              }}
              className="relative mb-8 text-[var(--color-primary)]"
            >
              <svg
                className="w-32 h-32 md:w-40 md:h-40 drop-shadow-2xl"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                <path d="M21 3v5h-5" />
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                <path d="M8 16H3v5" />
              </svg>
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <h2 className="text-3xl font-black text-[var(--color-text-primary)] mb-4">
                Обновление приложения
              </h2>
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <div className="flex flex-col gap-2 mb-6 max-w-sm">
                <div className="flex items-center gap-3 text-[var(--color-text-secondary)]">
                  <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-green-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <span>Загрузка новой версии</span>
                </div>
                <div className="flex items-center gap-3 text-[var(--color-text-secondary)]">
                  <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-green-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <span>Подготовка к запуску</span>
                </div>
                <div className="flex items-center gap-3 text-[var(--color-primary)] font-semibold">
                  <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center animate-pulse">
                    <svg
                      className="w-4 h-4 text-blue-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                  </div>
                  <span>Активация Service Worker</span>
                </div>
              </div>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-[var(--color-text-tertiary)] font-medium max-w-xs"
            >
              {progressText}
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PWAUpdatePrompt;
